<template>
  <app-page-layout page-class="msc-home-page" :show-notifications="true">
    <div class="msc-home-welcome" v-if="$root.isUserAuthenticated()">
      <p class="msc-home-welcome__eyebrow">mySchool.cafe</p>
      <h1 class="msc-home-welcome__title">Welcome back, {{ $root.alias() }}!</h1>
      <p class="msc-home-welcome__subtitle">
        Here’s what’s happening at school and a quick snapshot of your account.
      </p>
    </div>

    <section v-if="newsItems.length > 0" class="msc-home-section msc-home-news">
      <header class="msc-home-section__header">
        <h2 class="msc-home-section__title">Notes &amp; Happenings</h2>
        <p class="msc-home-section__subtitle">Updates and announcements from your school</p>
      </header>

      <div class="row msc-news-grid">
          <div
            v-for="(news, index) in newsItems"
            :key="index"
            class="col-md-6 col-lg-6 d-flex"
          >
            <article class="msc-news-card" :class="{ 'msc-news-card--new': isNewsNew(news) }">
              <div class="msc-news-card__icon" aria-hidden="true">
                <i :class="isNewsNew(news) ? 'now-ui-icons tech_watch-time' : 'now-ui-icons objects_globe'"></i>
              </div>
              <div class="msc-news-card__body">
                <b-badge v-if="isNewsNew(news)" pill variant="warning" class="msc-news-card__badge">New</b-badge>
                <h3 class="msc-news-card__title">{{ news.title }}</h3>
                <div class="msc-news-card__text" v-html="news.description"></div>
              </div>
            </article>
          </div>
        </div>
    </section>

    <section v-if="$root.isUserAuthenticated()" class="msc-home-section msc-home-stats">
      <header class="msc-home-section__header">
        <h2 class="msc-home-section__title">Your Account at a Glance</h2>
        <p class="msc-home-section__subtitle">Helpful details about how and when you’ve signed in</p>
      </header>

      <div class="msc-stats-panel">
          <div class="msc-stats-metrics">
            <div class="msc-stat-card">
              <div class="msc-stat-card__icon msc-stat-card__icon--count">
                <i class="now-ui-icons design-2_ruler-pencil"></i>
              </div>
              <div class="msc-stat-card__content">
                <span class="msc-stat-card__label">Logon Count</span>
                <span class="msc-stat-card__value">{{ $root.portal.logonCount }}</span>
              </div>
            </div>

            <div class="msc-stat-card">
              <div class="msc-stat-card__icon msc-stat-card__icon--ip">
                <i class="now-ui-icons ui-2_chat-round"></i>
              </div>
              <div class="msc-stat-card__content">
                <span class="msc-stat-card__label">Last IP Address</span>
                <span class="msc-stat-card__value">{{ $root.portal.lastIP || '—' }}</span>
              </div>
            </div>

            <div class="msc-stat-card">
              <div class="msc-stat-card__icon msc-stat-card__icon--date">
                <i class="now-ui-icons business_chart-pie-36"></i>
              </div>
              <div class="msc-stat-card__content">
                <span class="msc-stat-card__label">Last Logon</span>
                <span class="msc-stat-card__value msc-stat-card__value--date">
                  {{ formatLogonDate($root.portal.lastLogon) }}
                </span>
              </div>
            </div>
          </div>

          <div class="msc-stats-aside">
            <h3 class="msc-stats-aside__title">Access Log</h3>
            <p class="msc-stats-aside__text">
              See where and when your account was used. Handy if you want to double-check recent sign-ins.
            </p>
            <button
              type="button"
              class="btn btn-primary btn-round btn-sm msc-stats-aside__btn"
              @click="toggleShowAccessLog"
            >
              {{ showAccessLog ? 'Hide Access Log' : 'Show Access Log' }}
            </button>
          </div>
        </div>

        <div v-if="showAccessLog" class="msc-access-log">
          <h3 class="msc-access-log__title">Recent Sign-ins</h3>
          <el-table :data="$root.account.remoteAccessLog">
            <el-table-column width="60" min-width="60" type="index" sortable></el-table-column>
            <el-table-column min-width="200" prop="who" label="Who" sortable></el-table-column>
            <el-table-column min-width="150" prop="where" label="Where" sortable></el-table-column>
            <el-table-column min-width="200" prop="When" label="When" sortable>
              <template slot-scope="props">
                {{ moment(props.row.when).format('ddd, MMM Do YYYY, hh:mma') }}
              </template>
            </el-table-column>
            <el-table-column min-width="200" prop="notes" label="Description"></el-table-column>
          </el-table>
        </div>
    </section>
  </app-page-layout>
</template>
<script>
  import { Badge, Button, Card, FormGroupInput, TabPane, Tabs } from '@/components';
  import AppPageLayout from '@/layout/AppPageLayout.vue';
  import GeneralService from '@/services/GeneralService';
  import { Table, TableColumn } from 'element-ui';
  import Moment from 'moment';

  export default {
    name: 'app-main-page',
    components: {
      AppPageLayout,
      Card,
      Tabs,
      TabPane,
      Badge,
      [Table.name]: Table,
      [TableColumn.name]: TableColumn,
      [Button.name]: Button,
      [FormGroupInput.name]: FormGroupInput,
    },
    data() {
      return {
        showAccessLog: false,
        newsList: [],
        students: [],
      };
    },
    computed: {
      newsItems() {
        return this.newsList.filter(
          (news) => news.category === 'NEWS' && news.title.substring(0, 1) !== '-'
        );
      },
    },
    async mounted() {
      await Promise.all([this.getNews(), this.getStudents()]);

      if (this.students.length === 0) {
        this.$router.push('/app/profile/#addStudents');
      }
    },
    methods: {
      moment(d) {
        return Moment(d);
      },
      formatLogonDate(value) {
        const formatted = Moment(value);
        return formatted.isValid() ? formatted.format('DD-MMM-YY hh:mm A') : '—';
      },
      isNewsNew(news) {
        return this.$root.portal && news.updatedAt >= this.$root.portal.lastLogon;
      },
      toggleShowAccessLog() {
        this.showAccessLog = !this.showAccessLog;
      },
      async getStudents() {
        try {
          const response = await GeneralService.getStudents();
          this.students = response.data.students;
        } catch (error) {
          console.error(error);
        }
      },
      async getNews() {
        try {
          const response = await GeneralService.getNews();
          this.newsList = response.data;
        } catch (error) {
          this.newsList = [];
        }
      },
    },
  };
</script>
